/**
 * BLOCKS LAB — Code101 chapter 4, where a recipe stops being words and becomes an act.
 *
 * FOUNDER, 2026-08-14: "visual learning is much better than we just writing text… blocks etc",
 * then "ch4 blocks first".
 *
 * ── THE ONE THING THIS TEACHES ────────────────────────────────────────────────────────────────
 * The chapter's own unlock line: "the difference between the text of a program and the act of
 * running it — and therefore the difference between a bug you can read and a bug you can only
 * observe." So the screen is split in two and stays split: THE TEXT, which does nothing, and THE
 * MACHINE, which does it. A learner who has watched the same four lines sit still and then walk
 * has the distinction; one who has read a paragraph about it has a sentence.
 *
 * The three panels under the machine are the module's own three things, in his words — reading the
 * next line, keeping track of values, doing work you can see — not a UI designer's headings.
 *
 * ── WHY CLICK AND NOT DRAG ────────────────────────────────────────────────────────────────────
 * Every block palette a learner has seen is drag-and-drop, and drag is the wrong instinct here on
 * two counts. It needs a library, and this codebase's floor is zero dependency; and study8x runs on
 * cheap phones in villages where a long-press drag on a small target is a fight. Tap to add, tap to
 * change, arrows to reorder. Nothing is lost — the lesson is what running IS, not dexterity.
 *
 * ── WHAT IT REFUSES TO DO ─────────────────────────────────────────────────────────────────────
 * It never says "wrong". A semantic error is a program running CORRECTLY — that is the whole point
 * of §3 — and an instrument that flagged it would teach the opposite of the section it serves. When
 * the learner turns an add into a multiply, the machine reports 43,290 with no complaint, and the
 * lab offers one computed number beside it: what those same values add to. Arithmetic, not verdict.
 * The learner draws the conclusion, which is the only way it lands.
 *
 * It is also not a programming language and does not pretend to be: four operations, no branches,
 * no loops. The module says the machine goes "one line at a time, top to bottom, with branches and
 * loops" — branches and loops are named there and are NOT modelled here, which the surface says
 * out loud rather than leaving a learner to think he has seen everything.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  BLOCK_TYPES, STARTER, STARTER_SOURCE, toText, run, sumOfValues, breakSyntax,
  TWO_WRONGS, THREE_THINGS, THREE_THINGS_SOURCE,
} from './blocks.mjs';

const OPS = Object.keys(BLOCK_TYPES);

export default function BlocksLab() {
  const [prog, setProg] = useState(() => STARTER.map((b) => ({ ...b })));
  const [at, setAt] = useState(-1);            // which step the machine is on; -1 = at rest
  const [playing, setPlaying] = useState(false);
  const [ms, setMs] = useState(900);
  const [broken, setBroken] = useState(null);  // the manufactured syntax error, or null
  const timer = useRef(0);

  const trace = useMemo(() => run(prog), [prog]);
  const text = useMemo(() => toText(prog), [prog]);
  const step = at >= 0 ? trace.steps[at] : null;

  const stop = useCallback(() => { clearTimeout(timer.current); setPlaying(false); }, []);
  const reset = useCallback(() => { stop(); setAt(-1); }, [stop]);

  useEffect(() => {
    if (!playing) return undefined;
    if (at >= trace.steps.length - 1) { setPlaying(false); return undefined; }
    timer.current = setTimeout(() => setAt((i) => i + 1), ms);
    return () => clearTimeout(timer.current);
  }, [playing, at, ms, trace.steps.length]);
  useEffect(() => stop, [stop]);

  /* Editing the program puts the machine back at rest. A trace half-walked through a program that
     no longer exists would show slots from lines that are gone. */
  const edit = (fn) => { reset(); setBroken(null); setProg(fn); };
  const addBlock = (op) => edit((p) => [...p, op === 'show'
    ? { op, slot: 'total' } : { op, slot: 'total', value: 10 }]);
  const setOp = (i, op) => edit((p) => p.map((b, j) => (j === i ? { ...b, op, ...(op === 'show' ? {} : { value: b.value ?? 10 }) } : b)));
  const setVal = (i, v) => edit((p) => p.map((b, j) => (j === i ? { ...b, value: v } : b)));
  const move = (i, d) => edit((p) => { const q = p.slice(); const j = i + d;
    if (j < 0 || j >= q.length) return p; [q[i], q[j]] = [q[j], q[i]]; return q; });
  const drop = (i) => edit((p) => p.filter((_, j) => j !== i));

  const produced = trace.effects[trace.effects.length - 1] ?? null;
  const adds = sumOfValues(prog);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Blocks lab · the text, and the act</div>
      <h2 className="g-h2 lab-h">A recipe is not cooking</h2>
      <p className="g-body">
        Build a program out of blocks, then watch a machine follow it — one line at a time, keeping
        its slots, doing what you can see. The blocks are here so you can never mis-spell anything.
        That leaves exactly one way left to be wrong, and it is the one worth meeting.
      </p>

      {/* ── PALETTE ── */}
      <div className="lab-task-head">
        <span className="g-label">Add a block</span>
        {OPS.map((op) => (
          <button key={op} className="g-btn blk-add" onClick={() => addBlock(op)}>
            + {BLOCK_TYPES[op].verb}
          </button>
        ))}
        <button className="g-btn" onClick={() => edit(() => STARTER.map((b) => ({ ...b })))}>
          Back to the module's example
        </button>
      </div>

      <div className="blk-two">
        {/* ── THE TEXT, AT REST ── */}
        <div className="blk-pane">
          <div className="g-label">The program · blocks</div>
          <div className="blk-prog">
            {prog.length === 0 && <p className="g-body g-dim">No blocks. The machine has nothing to follow.</p>}
            {prog.map((b, i) => (
              <div key={i} className={`blk ${step && step.line === i ? 'blk-now' : ''}`}>
                <span className="blk-n">{i + 1}</span>
                <select className="blk-sel" value={b.op} onChange={(e) => setOp(i, e.target.value)}>
                  {OPS.map((op) => <option key={op} value={op}>{BLOCK_TYPES[op].verb}</option>)}
                </select>
                <span className="blk-slot">total</span>
                {b.op !== 'show' && (
                  <input className="blk-val" type="number" value={b.value}
                    onChange={(e) => setVal(i, Number(e.target.value))} />
                )}
                <span className="blk-sp" />
                <button className="blk-ico" onClick={() => move(i, -1)} aria-label="move up">↑</button>
                <button className="blk-ico" onClick={() => move(i, 1)} aria-label="move down">↓</button>
                <button className="blk-ico" onClick={() => drop(i)} aria-label="remove">×</button>
              </div>
            ))}
          </div>

          <div className="g-label" style={{ marginTop: 'var(--g-3)' }}>The same program · as text</div>
          <pre className={`blk-text ${broken ? 'blk-text-broken' : ''}`}>
            {(broken ? broken.text : text).split('\n').map((l, i) => (
              <span key={i} className={step && step.line === i && !broken ? 'blk-line blk-now-line' : 'blk-line'}>
                <i>{i + 1}</i>{l}
              </span>
            ))}
          </pre>
          <p className="g-body g-dim blk-rest">
            {broken
              ? 'The machine will not start. It cannot read this.'
              : 'On its own this does nothing at all. It is text in a file.'}
          </p>
        </div>

        {/* ── THE MACHINE, RUNNING ── */}
        <div className="blk-pane">
          <div className="lab-task-head">
            <button className="g-btn g-btn-primary" disabled={!prog.length || Boolean(broken)}
              onClick={() => { if (at >= trace.steps.length - 1) setAt(-1); setPlaying((p) => !p); }}>
              {playing ? 'Pause' : 'Run'}
            </button>
            <button className="g-btn" disabled={!prog.length || Boolean(broken) || at >= trace.steps.length - 1}
              onClick={() => { stop(); setAt((i) => Math.min(i + 1, trace.steps.length - 1)); }}>
              Step
            </button>
            <button className="g-btn" onClick={reset}>Reset</button>
          </div>
          <div className="lab-slider">
            <span className="g-label">A line every {(ms / 1000).toFixed(1)} s</span>
            <input type="range" min={200} max={2000} step={100} value={2200 - ms} className="lab-range"
              onChange={(e) => setMs(2200 - Number(e.target.value))} />
          </div>

          {/* The module's three things, named as he names them. */}
          <div className="blk-three">
            <div className="blk-cell">
              <span className="g-label">{THREE_THINGS[0].label}</span>
              <span className="lab-num">{step ? `line ${step.line + 1}` : '—'}</span>
              <code className="blk-cur">{step ? step.text : 'at rest'}</code>
            </div>
            <div className="blk-cell">
              <span className="g-label">{THREE_THINGS[1].label} · the slots</span>
              {step && Object.keys(step.slots).length
                ? Object.entries(step.slots).map(([k, v]) => (
                    <div key={k} className="blk-slotrow"><span>{k}</span><b>{v}</b></div>))
                : <span className="g-body g-dim">no slots yet</span>}
            </div>
            <div className="blk-cell">
              <span className="g-label">{THREE_THINGS[2].label}</span>
              {at >= 0 && trace.steps.slice(0, at + 1).some((s) => s.effect)
                ? trace.steps.slice(0, at + 1).filter((s) => s.effect)
                    .map((s, i) => <div key={i} className="blk-out">{s.effect}</div>)
                : <span className="g-body g-dim">nothing yet</span>}
            </div>
          </div>
          {step?.note && <p className="g-body morse-warn">{step.note}</p>}
          <p className="g-body g-dim blk-src"><Value of={cited(THREE_THINGS[2].quote, THREE_THINGS_SOURCE)} /></p>
        </div>
      </div>

      {/* ── THE TWO WAYS TO BE WRONG ─────────────────────────────────────────────────────────
       * Section 3's whole argument, made operable. The syntax half is manufactured on the TEXT,
       * because blocks cannot produce it — and that impossibility is itself the point, said out
       * loud rather than quietly enjoyed. */}
      <div className="lab-task">
        <div className="g-label">Two ways to be wrong</div>
        <div className="blk-wrongs">
          <div className="blk-wrong">
            <b>Syntax</b>
            <p className="g-body"><Value of={cited(TWO_WRONGS.syntax.what, TWO_WRONGS.syntax.source)} /></p>
            <p className="g-body g-dim">{TWO_WRONGS.syntax.consequence}</p>
            <button className="g-btn" onClick={() => { reset(); setBroken(broken ? null : breakSyntax(prog)); }}
              disabled={!prog.length}>
              {broken ? 'Put the bracket back' : 'Take a bracket out of the text'}
            </button>
            {broken && <p className="g-body blk-err">{broken.error}</p>}
            <p className="g-body g-dim blk-note">
              You could not have made this with the blocks — they only fit one way. That is what a
              block palette is for, and it is why the other kind of wrong is the one that matters.
            </p>
          </div>
          <div className="blk-wrong">
            <b>Semantics</b>
            <p className="g-body"><Value of={cited(TWO_WRONGS.semantic.what, TWO_WRONGS.semantic.source)} /></p>
            <p className="g-body g-dim">{TWO_WRONGS.semantic.consequence}</p>
            <button className="g-btn" onClick={() => { const i = prog.findIndex((b) => b.op === 'add');
              if (i >= 0) setOp(i, 'mul'); }} disabled={!prog.some((b) => b.op === 'add')}>
              Turn one “add” into a “multiply”
            </button>
            {/* COMPUTED, never judged. Two numbers side by side; the learner decides. */}
            <div className="lab-readout" style={{ marginTop: 'var(--g-2)' }}>
              <div><span className="g-label">The program produced</span>
                <Value of={produced === null ? computed('— run it') : computed(produced)} /></div>
              <div><span className="g-label">Those values add to</span>
                <Value of={computed(String(adds))} /></div>
            </div>
            <p className="g-body g-dim blk-note">
              The machine did not complain. It never will. Nothing on this screen calls it wrong,
              because it is not wrong — it is a program doing exactly what it says.
            </p>
          </div>
        </div>
      </div>

      <p className="g-body g-dim lab-why">
        Four operations, no branches and no loops. The module says a machine reads “one line at a
        time, top to bottom, with branches and loops (jumps to other parts of the file)” — the jumps
        are real and are not modelled here. This is what <b>running</b> is, not what a language has.
        <br />
        <Value of={cited('total = 240 + 180 + 90 · print(total)', STARTER_SOURCE)} />
      </p>
    </div>
  );
}
